/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { SVGIcon, SVGIconProps } from "@nordcloud/gnui";
import { CloudProviderType, Provider } from "~/generated/graphql";

// Some components take "error" status prop, meanwhile GNUI requires "danger"
export function mapInputStatus(
  input: "error" | "success" | undefined
): "danger" | "success" | undefined {
  return input === "error" ? "danger" : input;
}

type ProviderLowercase = Lowercase<(typeof Provider)[keyof typeof Provider]>;

type IconName = SVGIconProps["name"];

// "info" is the default icon for unsupported providers
// "ibmCloud" is workaround of bug with lowercase provider naming and icon naming in camel case.
type ProviderIconName =
  | "aks"
  | "eks"
  | "externalIntegration"
  | "gke"
  | "hybridCloud"
  | "ibmCloud"
  | "info"
  | (IconName & ProviderLowercase);

const SPECIAL_PROVIDER_ICONS: { [key: string]: ProviderIconName } = {
  [`${Provider.Ibmcloud}`]: "ibmCloud",
  [`${Provider.Hybrid}`]: "hybridCloud",
  [`${Provider.Aws}:${CloudProviderType.Kubernetes}`]: "eks",
  [`${Provider.Azure}:${CloudProviderType.Kubernetes}`]: "aks",
  [`${Provider.Gcp}:${CloudProviderType.Kubernetes}`]: "gke",
  [`${Provider.Hybrid}:${CloudProviderType.Kubernetes}`]: "kubernetes",
  [`${Provider.Hybrid}:${CloudProviderType.Openshift}`]: "openshift",
  [`${Provider.Hybrid}:${CloudProviderType.Vmware}`]: "vmware",
};

export function getProviderIconName(
  provider: Provider,
  cloudProviderType?: CloudProviderType
): ProviderIconName {
  if (provider === Provider.External) {
    return "externalIntegration";
  }
  if ([Provider.Services, Provider.Nordcloud].includes(provider)) {
    return "info";
  }
  const icon =
    SPECIAL_PROVIDER_ICONS[`${provider}:${cloudProviderType}`] ??
    SPECIAL_PROVIDER_ICONS[`${provider}`];

  return icon ?? (provider.toLocaleLowerCase() as ProviderIconName);
}

export function getIcon(input: string): IconName {
  if (input.toLocaleLowerCase() === "ibmcloud") {
    return "ibmCloud";
  }
  return input === "hybrid"
    ? "hybridCloud"
    : (input.toLocaleLowerCase() as IconName);
}

const CONNECTED_COMMENT = "Account is connected";
const DISCOVERED_COMMENT_HYBRID =
  "This cloud account was automatically discovered by scanning public cloud accounts. To scan resources running in your cluster please proceed with onboarding procedure.";
const BILLING_COMMENT =
  "IBM Multicloud Accelerator has access only for billing resources. Please provide cloud credentials to this account.";
const INACTIVE_COMMENT_HYBRID =
  "No data has been received recently. Please check that scanner is deployed and running successfully.";

export type AccountStatus = {
  color: "danger" | "notification" | "success" | "warning";
  name: IconName;
  comment: string;
  width: string;
};

const success: AccountStatus = {
  color: "success",
  name: "statusSuccess",
  comment: CONNECTED_COMMENT,
  width: "8rem",
};

const danger = (message: string): AccountStatus => {
  return {
    color: "danger",
    name: "statusError",
    comment: message,
    width: "18rem",
  };
};

const warning = (message: string): AccountStatus => {
  return {
    color: "warning",
    name: "statusWarning",
    comment: message,
    width: "18rem",
  };
};

const hybridProviders = [
  Provider.Kubernetes,
  Provider.Openshift,
  Provider.Hybrid,
];

const cloudIconStatus = (
  active: boolean,
  status: string,
  providerType?: string
): AccountStatus => {
  if (providerType === null || providerType === CloudProviderType.Cloud) {
    return active ? success : warning(BILLING_COMMENT);
  }
  return hybridIconStatus(status);
};

const hybridIconStatus = (status: string): AccountStatus => {
  switch (status) {
    case "active":
      return success;
    case "inactive":
      return warning(INACTIVE_COMMENT_HYBRID);
    default:
      return danger(DISCOVERED_COMMENT_HYBRID);
  }
};

export const getAccountIconStatus = ({
  provider,
  active,
  status,
  providerType,
}: {
  provider: Provider;
  active: boolean;
  status: string;
  providerType: CloudProviderType | undefined;
}): AccountStatus =>
  hybridProviders.includes(provider)
    ? hybridIconStatus(status)
    : cloudIconStatus(active, status, providerType);

export function getTypedKeys<T>(object: T): (keyof T)[] {
  // @ts-expect-error ignored temporary after migration to TypeScript 4.9
  return Object.keys(object) as (keyof typeof object)[];
}

// Source: https://github.com/bluelovers/ws-ts-type/blob/master/packages/ts-type-object-entries/src/index.ts
export function getTypedEntries<T, K extends string = string>(
  object: ArrayLike<T> | { [s in K]?: T }
): [K, T][] {
  return Object.entries(object) as [K, T][];
}

export type StrNumBool = boolean | number | string;

export type AllKeys<T> = T extends unknown ? keyof T : never;

type PickType<T, K extends AllKeys<T>> = T extends { [k in K]?: unknown }
  ? T[K]
  : undefined;

export type Merge<T extends Record<string, unknown>> = {
  [k in AllKeys<T>]: PickType<T, k>;
};

export type WithChildren<T = Record<string, unknown>> = T & {
  children?: React.ReactNode;
};

export type Maybe<T> = T | null | undefined;

export type PathName = React.ComponentProps<typeof SVGIcon>["name"];

export const isSomeEnum =
  <T>(e: T) =>
  (token: unknown): token is T[keyof T] =>
    // @ts-expect-error ignored temporary after migration to TypeScript 4.9
    Object.values(e).includes(token as T[keyof T]);

export type MixedValue =
  | Record<string, unknown>
  | number
  | string
  | null
  | undefined;

export type ResourceLifecycleDates =
  | {
      creationDate: string;
      currentMonthLastSpendDate: string;
    }
  | undefined;

export type StyledProps = {
  className?: string;
};
